import React, { useCallback, useEffect, useState } from "react";
import { MdOutlineSmartToy } from "react-icons/md";
import * as ai from "../../services/aiAssistantService";
import SettingsTab from "./SettingsTab";
import FaqTab from "./FaqTab";
import ConversationsTab from "./ConversationsTab";
import CallbacksTab from "./CallbacksTab";
import UsageTab from "./UsageTab";
import { modeLabel } from "./aiHelpers";

const TABS = [
  { key: "settings", label: "Settings" },
  { key: "faq", label: "FAQ" },
  { key: "conversations", label: "Conversations" },
  { key: "callbacks", label: "Callbacks" },
  { key: "usage", label: "Usage" }
];

const MODE_BADGE = { off: "text-bg-secondary", shadow: "text-bg-warning", live: "text-bg-success" };

// The vendor's control room for the AI support assistant: when it answers, in which languages, what it
// may quote (the FAQ), and how it has been doing. Vendor owner and admins only (the API checks again).
const AiAssistant = () => {
  const [tab, setTab] = useState("settings");
  const [state, setState] = useState({ loading: true, error: null, data: null });
  // Bumped after every save so the settings form restarts from the saved values.
  const [version, setVersion] = useState(0);

  const load = useCallback(async () => {
    try {
      const data = await ai.getSettings();
      setState({ loading: false, error: null, data });
    } catch (e) {
      setState({ loading: false, error: (e && e.message) || "Could not load the AI Assistant", data: null });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const { loading, error, data } = state;

  let body;
  if (loading) {
    body = (
      <div className="text-center text-muted py-5">
        <div className="spinner-border spinner-border-sm me-2" role="status" />
        Loading…
      </div>
    );
  } else if (error) {
    body = (
      <div className="alert alert-danger d-flex justify-content-between align-items-center" role="alert">
        <span>{error}</span>
        <button className="btn btn-sm btn-outline-danger" onClick={load}>
          Try again
        </button>
      </div>
    );
  } else if (!data || !data.configured) {
    body = (
      <div className="card shadow-sm rounded-4">
        <div className="card-body py-5 text-center">
          <MdOutlineSmartToy size={40} className="text-muted mb-3" />
          <h6 className="fw-semibold">The AI Assistant is not set up for your account yet</h6>
          <p className="text-muted small mb-0">
            {data && data.reason === "schema_missing"
              ? "It has not been installed on this platform yet. Please contact UtilStop."
              : "Ask UtilStop to enable it for your account, then come back here to choose how it should work."}
          </p>
        </div>
      </div>
    );
  } else {
    const s = data.settings;
    body = (
      <>
        {!data.platform_enabled && (
          <div className="alert alert-warning small" role="alert">
            The assistant is switched off across the whole platform at the moment, so it will not write or send anything, whatever mode you choose
            below. UtilStop can switch it on.
          </div>
        )}
        {!data.plan_allowed && (
          <div className="alert alert-warning small" role="alert">
            The AI Assistant is not included in your current plan, so it cannot be switched on.
          </div>
        )}

        <ul className="nav nav-tabs mb-3" role="tablist">
          {TABS.map((t) => (
            <li className="nav-item" key={t.key} role="presentation">
              <button
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                className={`nav-link ${tab === t.key ? "active" : ""}`}
                onClick={() => setTab(t.key)}
              >
                {t.label}
              </button>
            </li>
          ))}
        </ul>

        {tab === "settings" && (
          <SettingsTab
            key={version}
            data={data}
            onSaved={(next) => {
              setState({ loading: false, error: null, data: next });
              setVersion((v) => v + 1);
            }}
          />
        )}
        {tab === "faq" && <FaqTab />}
        {tab === "conversations" && <ConversationsTab />}
        {tab === "callbacks" && <CallbacksTab />}
        {tab === "usage" && <UsageTab settings={s} usage={data.usage} />}
      </>
    );
  }

  return (
    <div className="container-fluid px-4 py-4" style={{ maxWidth: 1100 }}>
      <div className="d-flex align-items-center gap-2 mb-3">
        <MdOutlineSmartToy size={24} />
        <h5 className="mb-0 fw-semibold">AI Assistant</h5>
        {data && data.configured && (
          <span className={`badge ${MODE_BADGE[data.settings.mode] || "text-bg-secondary"}`} style={{ fontSize: 11 }}>
            {modeLabel(data.settings.mode)}
          </span>
        )}
      </div>
      {body}
    </div>
  );
};

export default AiAssistant;
